/**
 * 每條路由自己的 `<title>`／`<meta description>`／`<link rel=canonical>`。
 *
 * ## 為什麼需要它
 *
 * 這是一個 SPA：`index.html` 只有一份，所以在此之前**全站 38 條路由共用同一個標題**。
 * 分享 `/privacy` 到 LINE 跟分享首頁，預覽卡片一模一樣；瀏覽器分頁列上也全都長一樣。
 *
 * canonical 更嚴重一點：新舊網域並存（`convo-web-kpku.onrender.com` 回的是位元組
 * 完全相同的內容，而 `.onrender.com` 主機名 Render 不給關，301 這條路不存在），
 * 沒有 canonical 就是兩份重複內容互相稀釋。
 *
 * ## 誠實說明它的極限
 *
 * 這是**用 JS 設的**。Googlebot 會執行 JS 所以看得到，但 LINE／FB／X 的預覽爬蟲
 * **只讀原始 HTML 的 `<head>`，不執行 JS**——對它們來說，每個網址拿到的還是
 * `index.html` 裡那一份靜態標籤。
 *
 * 也就是說：這個檔解決的是「搜尋引擎眼中的每頁差異」與「瀏覽器分頁標題」，
 * 解決不了「分享出去的卡片」。後者靠 build 時預渲染（`lib/prerender-head.ts`）。
 * 兩邊讀的是同一份值（`lib/public-heads.ts`）——分成兩份的時候，首頁標題就分岔過（E6-08）。
 */

const SITE_NAME = 'Talkomo';
/** canonical 一律指向正式網域，不看使用者是從哪個主機名進來的。 */
const CANONICAL_ORIGIN = 'https://talkomo.com';

export interface DocumentHead {
  title: string;
  description: string;
  /** 相對路徑（`/privacy`）。省略＝不宣告 canonical（後台頁不需要）。 */
  canonicalPath?: string;
}

/** 已經帶站名的（首頁那句「Talkomo — 用聊天做生意」）照用，其餘補上「｜Talkomo」。 */
export function formatTitle(title: string): string {
  return title.includes(SITE_NAME) ? title : `${title}｜${SITE_NAME}`;
}

export function canonicalUrl(path: string): string {
  return `${CANONICAL_ORIGIN}${path}`;
}

export function applyDocumentHead(head: DocumentHead): void {
  document.title = formatTitle(head.title);

  setMeta('description', head.description);
  setCanonical(head.canonicalPath);
}

/** 允許被索引的路徑。其餘一律標 noindex。 */
const INDEXABLE_PATHS = new Set(['/', '/privacy', '/terms']);

/**
 * 不在白名單上的路徑加上 `noindex`。
 *
 * ## 為什麼需要它
 *
 * `render.yaml` 的 `/*` → `index.html` 讓**任何**網址都回 200 HTML——
 * 實測 `/this-page-does-not-exist-12345` 也是 200。封測期間 `index.html` 有一行
 * 全站 noindex 蓋著，所以看不出問題；那一行拿掉的當天，每一個亂連的網址都會變成
 * 一個「內容跟登入頁一樣」的可索引頁面。
 *
 * ## 為什麼只加不減
 *
 * 這個函式**永遠不會移除**既有的 robots meta。那是刻意的：封測期間
 * `index.html` 裡那一行全站 noindex 必須繼續有效，而 Googlebot 會執行 JS
 * ——如果這裡在首頁把它改成 `index, follow`，等於用 JS 把封測期的門打開。
 *
 * 於是兩個階段都對：現在全站照樣不被索引；開放註冊拿掉那一行之後，
 * 白名單外的路徑由這裡接手，白名單內的因為「沒有 robots meta」而預設可索引。
 *
 * ## canonical 也在這裡拿掉
 *
 * 白名單外的路徑拿到的是 `index.html`，而那份 head 是首頁的——包括
 * `<link rel="canonical" href="https://talkomo.com/">`。於是 `/login`、後台、
 * 亂打的網址全都宣告「我是首頁的複本」（Lighthouse 判 canonical 不合格，E6-08）。
 * 那些頁面沒有任何一頁該有 canonical，拿掉就好。
 */
export function markIndexability(pathname: string): void {
  if (INDEXABLE_PATHS.has(pathname)) {
    return;
  }

  setCanonical(undefined);

  if (document.querySelector('meta[name="robots"]') !== null) {
    return;
  }

  setMeta('robots', 'noindex, nofollow');
}

function setMeta(name: string, content: string): void {
  const existing = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (existing !== null) {
    existing.content = content;
    return;
  }

  const meta = document.createElement('meta');
  meta.name = name;
  meta.content = content;
  document.head.append(meta);
}

function setCanonical(path: string | undefined): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (path === undefined) {
    existing?.remove();
    return;
  }

  const href = canonicalUrl(path);

  if (existing !== null) {
    existing.href = href;
    return;
  }

  const link = document.createElement('link');
  link.rel = 'canonical';
  link.href = href;
  document.head.append(link);
}
